import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography
} from "@material-ui/core";

import {
  AddBox,
  Close,
  GetApp,
  Share,
  Smartphone
} from "@material-ui/icons";

const isStandaloneMode = () =>
  window.matchMedia?.(
    "(display-mode: standalone)"
  )?.matches ||
  window.navigator.standalone === true;

const InstallKoraxCard = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState(null);

  const [installed, setInstalled] =
    useState(isStandaloneMode());

  const [iosHelpOpen, setIosHelpOpen] =
    useState(false);

  const [installing, setInstalling] =
    useState(false);

  const isIos = useMemo(() => {
    const userAgent =
      window.navigator.userAgent || "";

    const isAppleDevice =
      /iPad|iPhone|iPod/i.test(userAgent);

    const isIpadDesktopMode =
      window.navigator.platform === "MacIntel" &&
      window.navigator.maxTouchPoints > 1;

    return isAppleDevice || isIpadDesktopMode;
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = event => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setInstalling(false);
      setIosHelpOpen(false);
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt
    );

    window.addEventListener(
      "appinstalled",
      handleAppInstalled
    );

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );

      window.removeEventListener(
        "appinstalled",
        handleAppInstalled
      );
    };
  }, []);

  const handleInstall = async () => {
    if (isIos) {
      setIosHelpOpen(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    try {
      setInstalling(true);

      await deferredPrompt.prompt();

      const choice =
        await deferredPrompt.userChoice;

      if (choice?.outcome === "accepted") {
        setInstalled(true);
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.warn(
        "[Korax PWA] Não foi possível iniciar a instalação:",
        error
      );
    } finally {
      setInstalling(false);
    }
  };

  if (installed) {
    return null;
  }

  if (!isIos && !deferredPrompt) {
    return null;
  }

  return (
    <>
      <Box
        style={{
          marginTop: 14,
          padding: 14,
          borderRadius: 16,
          border:
            "1px solid rgba(96,165,250,.25)",
          background:
            "linear-gradient(135deg, rgba(37,99,235,.15), rgba(79,70,229,.08))",
          boxShadow:
            "0 12px 28px rgba(15,23,42,.16)"
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          style={{ gap: 11 }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: 38,
              minWidth: 38,
              height: 38,
              borderRadius: 12,
              background:
                "rgba(96,165,250,.16)",
              color: "#93C5FD"
            }}
          >
            <Smartphone fontSize="small" />
          </Box>

          <Box style={{ flex: 1 }}>
            <Typography
              style={{
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 800
              }}
            >
              Instale a Korax
            </Typography>

            <Typography
              style={{
                marginTop: 3,
                color:
                  "rgba(255,255,255,.60)",
                fontSize: 11,
                lineHeight: 1.5
              }}
            >
              Acesse sua operação mais rápido,
              em tela cheia e com um toque.
            </Typography>
          </Box>
        </Box>

        <Button
          type="button"
          fullWidth
          startIcon={<GetApp />}
          onClick={handleInstall}
          disabled={installing}
          style={{
            marginTop: 12,
            minHeight: 40,
            borderRadius: 12,
            color: "#FFFFFF",
            background:
              "linear-gradient(90deg, #2563EB, #4F46E5)",
            fontSize: 12,
            fontWeight: 800,
            textTransform: "none",
            boxShadow:
              "0 10px 22px rgba(37,99,235,.28)"
          }}
        >
          {installing
            ? "Preparando instalação..."
            : isIos
              ? "Adicionar à tela inicial"
              : "Instalar Korax"}
        </Button>
      </Box>

      <Dialog
        open={iosHelpOpen}
        onClose={() => setIosHelpOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 22,
            background: "#0F172A",
            color: "#FFFFFF",
            border:
              "1px solid rgba(96,165,250,.25)"
          }
        }}
      >
        <DialogContent
          style={{
            position: "relative",
            padding: "26px 22px 24px"
          }}
        >
          <IconButton
            onClick={() => setIosHelpOpen(false)}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              color:
                "rgba(255,255,255,.68)"
            }}
          >
            <Close />
          </IconButton>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: 52,
              height: 52,
              margin: "0 auto 16px",
              borderRadius: 16,
              color: "#93C5FD",
              background:
                "rgba(59,130,246,.15)"
            }}
          >
            <Smartphone />
          </Box>

          <Typography
            align="center"
            style={{
              fontSize: 19,
              fontWeight: 900
            }}
          >
            Instale a Korax no iPhone
          </Typography>

          <Typography
            align="center"
            style={{
              marginTop: 7,
              color:
                "rgba(255,255,255,.62)",
              fontSize: 12,
              lineHeight: 1.55
            }}
          >
            A Apple exige dois passos rápidos
            para adicionar o aplicativo.
          </Typography>

          <Box
            style={{
              marginTop: 22,
              padding: 15,
              borderRadius: 15,
              background:
                "rgba(255,255,255,.05)",
              border:
                "1px solid rgba(255,255,255,.08)"
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 12 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 38,
                  minWidth: 38,
                  height: 38,
                  borderRadius: 12,
                  color: "#60A5FA",
                  background:
                    "rgba(59,130,246,.13)"
                }}
              >
                <Share />
              </Box>

              <Box>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 800
                  }}
                >
                  1. Toque em Compartilhar
                </Typography>

                <Typography
                  style={{
                    marginTop: 2,
                    color:
                      "rgba(255,255,255,.54)",
                    fontSize: 11
                  }}
                >
                  Use o botão de compartilhar do Safari.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            style={{
              marginTop: 10,
              padding: 15,
              borderRadius: 15,
              background:
                "rgba(255,255,255,.05)",
              border:
                "1px solid rgba(255,255,255,.08)"
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 12 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 38,
                  minWidth: 38,
                  height: 38,
                  borderRadius: 12,
                  color: "#4ADE80",
                  background:
                    "rgba(34,197,94,.12)"
                }}
              >
                <AddBox />
              </Box>

              <Box>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 800
                  }}
                >
                  2. Adicionar à Tela de Início
                </Typography>

                <Typography
                  style={{
                    marginTop: 2,
                    color:
                      "rgba(255,255,255,.54)",
                    fontSize: 11
                  }}
                >
                  Confirme e o ícone da Korax aparecerá.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            type="button"
            fullWidth
            onClick={() => setIosHelpOpen(false)}
            style={{
              marginTop: 20,
              minHeight: 42,
              borderRadius: 12,
              color: "#FFFFFF",
              background:
                "linear-gradient(90deg, #2563EB, #4F46E5)",
              fontWeight: 800,
              textTransform: "none"
            }}
          >
            Entendi
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallKoraxCard;
